/*
  Ever Leaf Product Section — Materials/Color scroll assist. After the
  customer interacts with a Material swatch (Step 3, the final
  personalization step, labeled "Color: <value>" in the storefront UI —
  this is the "Variants: Color" trigger the PASS 3A FINAL CONSOLIDATED UI
  CORRECTION brief refers to) with a photo already uploaded, smoothly moves
  the viewport so Add to Cart lands at the VERTICAL CENTER of the visible
  screen.

  PASS 3A FINAL CONSOLIDATED UI CORRECTION (previous pass), Section 38-44
  — root cause of "Add to Cart lands too close to the TOP": the original
  targeting math aimed for a small, fixed offset below the header (roughly
  80-140px from the top of the viewport), not the viewport's center. Fixed
  by retargeting the SAME existing trigger at
  `viewportCenter = (ATC's own vertical center)`. That same pass also
  EXTENDED this from Mobile-only to Desktop+Mobile — flagged explicitly in
  that pass's own report as an interpretation, not a certain reading of the
  brief.

  PASS 3A FINAL CONSOLIDATED CALIBRATION + FRAMING CORRECTION (this pass),
  Section 36-37 — real user DEV QA came back explicit and unambiguous:
  Mobile centered scroll = PASS; Desktop auto-scroll is NOT wanted, remove
  it. Restored the Mobile-only gate this file originally shipped with — the
  centering MATH itself (Section 37 — "freeze... keep current centering
  math, visualViewport.height use, reduced motion handling") is completely
  unchanged, only the platform gate is restored. This file's name predates
  the brief that temporarily extended it to Desktop — kept as-is (freeze
  passed areas unless unavoidable; a rename would touch the <script src>
  reference in sections/ever-leaf-product.liquid for a purely cosmetic
  reason).

  Deliberately separate from assets/ever-leaf-personalizer.js — this is a
  pure UX convenience layer with zero purchase-state authority. It never
  reads or writes PersonalizerState, never clicks/focuses/checks anything,
  and never prevents Dawn's own real swatch-click -> variant-selects ->
  `change` event flow (passive `click` listener only, no preventDefault).

  Trigger: a real click/tap on the swatch LABEL (the actual visible,
  interactive element — not the visually-hidden native radio, and not
  reliant on the `change` event, which does not fire when the customer taps
  the swatch that is already selected).

  Conditions (all must hold, checked at the moment of the tap):
  - Mobile viewport only (matches this project's existing `max-width:749px`
    breakpoint, used everywhere else in this theme's own CSS) — restored
    this pass; Desktop must never auto-scroll.
  - `.ever-leaf-product.ever-leaf-has-photo` is present — the SAME
    authoritative photo-state flag assets/ever-leaf-personalizer.js already
    drives Add to Cart's disabled state from. No second photo-state system.
  - Add to Cart is not disabled at the moment the scroll would fire.

  Never triggers on page load, while typing Dedication, or merely because a
  photo exists with no Step-3 interaction. Never scrolls on Desktop. Never
  scrolls toward Buy Now or Terms. Fires at most once per tap (a short
  re-entrancy guard prevents a single physical tap from ever producing two
  scrolls); does not fight a manual scroll afterward — this is a one-shot
  assist, not a scroll-position observer.
*/
(function () {
  'use strict';

  if (window.EverLeafMobileScrollAssist) return;

  var MOBILE_BREAKPOINT = '(max-width: 749px)';
  var SCROLL_DURATION_MS = 350; // within the requested ~250-450ms quick-feel range
  var GUARD_RELEASE_MS = 600; // blocks an accidental duplicate trigger from the same tap without blocking a later, real, separate tap
  var scrollGuardActive = false;

  function isMobile() {
    return window.matchMedia(MOBILE_BREAKPOINT).matches;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function hasPhoto() {
    var root = document.querySelector('.ever-leaf-product');
    return !!(root && root.classList.contains('ever-leaf-has-photo'));
  }

  function getAddToCartButton() {
    return document.querySelector('.ever-leaf-product button[name="add"]');
  }

  // Section 43 — accounts for window.visualViewport.height on Mobile (the
  // actually-visible viewport height, correctly excluding mobile browser
  // chrome/an open on-screen keyboard), falling back to window.innerHeight
  // where visualViewport isn't available.
  function getViewportHeight() {
    return window.visualViewport ? window.visualViewport.height : window.innerHeight;
  }

  /* Small, fixed-duration rAF scroller — native `scrollTo({behavior:
     'smooth'})` duration scales with distance and can feel sluggish over
     the distance from Materials to Add to Cart; this keeps the perceived
     speed consistent regardless of how far the scroll needs to travel. */
  function animateScrollTo(targetY, duration) {
    var startY = window.scrollY;
    var delta = targetY - startY;
    if (Math.abs(delta) < 2) return;
    var startTime = null;

    function easeOutQuad(t) {
      return 1 - (1 - t) * (1 - t);
    }

    function step(timestamp) {
      if (startTime === null) startTime = timestamp;
      var elapsed = timestamp - startTime;
      var progress = Math.min(elapsed / duration, 1);
      window.scrollTo(0, startY + delta * easeOutQuad(progress));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    }
    window.requestAnimationFrame(step);
  }

  /*
    Section 41 — centered scroll geometry:
      targetViewportCenter = viewportHeight / 2
      ATCCenter = rect.top + rect.height / 2
    Scrolls by the difference between the two, so Add to Cart's own
    vertical center lands at the viewport's vertical center. Replaces the
    prior fixed-header-offset targeting entirely.
  */
  function performScroll() {
    var atc = getAddToCartButton();
    if (!atc || atc.disabled) return;

    var rect = atc.getBoundingClientRect();
    var atcCenter = rect.top + rect.height / 2;
    var targetViewportCenter = getViewportHeight() / 2;
    var targetY = Math.max(window.scrollY + (atcCenter - targetViewportCenter), 0);

    if (prefersReducedMotion()) {
      window.scrollTo(0, targetY);
      return;
    }
    animateScrollTo(targetY, SCROLL_DURATION_MS);
  }

  // MOBILE IMMERSIVE PRODUCT STAGE — Section 32 of the brief. This assist
  // assumed Materials sits vertically above a normal-flow ATC; once the new
  // Mobile Stage is active, Materials lives inside the Configuration
  // Carousel and ATC lives inside the same immersive stage's Purchase Dock
  // — scrolling the page after a Material tap would fight that layout
  // (there is nothing useful to scroll to). Narrowly gated off here rather
  // than deleted, so it still functions correctly if the new stage is ever
  // disabled (e.g. assets/ever-leaf-mobile-stage.js fails to load/run).
  function isImmersiveStageActive() {
    var root = document.querySelector('.ever-leaf-product');
    return !!(root && root.hasAttribute('data-ever-leaf-mobile-stage-active'));
  }

  function handleMaterialInteraction() {
    if (!isMobile()) return;
    if (!hasPhoto()) return;
    if (isImmersiveStageActive()) return;
    if (scrollGuardActive) return;
    scrollGuardActive = true;

    /* Let Dawn's real variant-selects update (checked state, price, ATC's
       own disabled/enabled state from ever-leaf-personalizer.js's gating)
       settle before measuring where Add to Cart now sits. Two rAFs is a
       deliberately short, non-arbitrary wait — one for the click's own
       synchronous DOM effects, one for the paint that follows. */
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        performScroll();
        window.setTimeout(function () {
          scrollGuardActive = false;
        }, GUARD_RELEASE_MS);
      });
    });
  }

  function init() {
    var materialsBlock = document.querySelector('.ever-leaf-product__materials');
    if (!materialsBlock) return;

    materialsBlock.addEventListener('click', function (event) {
      var interactive = event.target.closest('.swatch-input__label, .ever-leaf-materials__item');
      if (!interactive) return;
      handleMaterialInteraction();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.EverLeafMobileScrollAssist = {
    performScroll: performScroll
  };
})();
